import React, { useEffect, useState, useRef } from 'react';
import setting from '../../../config.json';
import TauriDatePickerPlanning from './TauriDatePickerPlanning/TauriDatePickerPlanning';
import TauriSidebarFilterPlanning from './TauriSidebarFilterPlanning/TauriSidebarFilterPlanning';
import TauriLoader2 from '../../TauriLoader2/TauriLoader2';
import './PlanningSidebar.css';
import { getAll, getRecordById } from '../../../localDB';
import OpalAccordionItem from '../../OpalAccordionItem/OpalAccordionItem';
import TauriImage from '../../TauriImage/TauriImage';
import { Button, Dropdown } from 'react-bootstrap';
import AccountDetailsModal from '../../OpalCoverageLayout/AccountDetailsModal/AccountDetailsModal';

const groupByOptions = [
    { key: false, label: "No Grouping" },
    { key: "InstitutionName", label: "Institutions" },
    { key: "Area", label: "Area/Territory" },
];

const orderByOptions = [
    { key: "Code", label: "Code" },
    { key: "Name", label: "Name" }
];

const PlanningSidebar = ({ selectedItem, onDeleteItem, worksheet, selectedDate }) => {
    const [availableItemList, setAvailableItemList] = useState([]);
    const [selectedItemList, setSelectedItemList] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState({
        orderby: orderByOptions[0].key,
        groupby: groupByOptions[1].key,
    });
    const [showMenu, setShowMenu] = useState(false);

    const [selectedCard, setSelectedCard] = useState(null);
    const [activeTab, setActiveTab] = useState("available");
    const [selectedAccID, setSelectedAccID] = useState(null);
    const [showAccountDetails, setShowAccountDetails] = useState(false);

    const menuRef = useRef();
    const getAccountDatesInDays = (worksheet, accountID) => {
        if (!worksheet?.Days) return [];

        return Object.entries(worksheet.Days)
            .filter(([date, day]) =>
                (day?.accounts || []).some(acc => acc.accountID === accountID)
            )
            .map(([date]) => date); // return only the dates
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleRefresh = () => {
            fetchData(search, selectedFilter.orderby);
        };

        handleRefresh();

        window.addEventListener("RefreshPlanningAccounts", handleRefresh);

        return () => {
            window.removeEventListener("RefreshPlanningAccounts", handleRefresh)
        }
    }, [search, selectedFilter, worksheet, selectedDate]);

    const fetchData = async (search) => {
        try {
            const planningAccount = await getAll("PlanningAccounts");
            const lowercasedValue = search.toLowerCase();

            const mergedList = await Promise.all(
                planningAccount.map(async (account) => {
                    const customer = await getRecordById("customer", account.ID);
                    const institution = customer
                        ? await getRecordById("institution", customer.InstitutionID)
                        : null;

                    const accountDates = getAccountDatesInDays(worksheet, account.ID);

                    return {
                        ...account,
                        Customer: {
                            ...customer,
                            InstitutionName: institution?.Name || null
                        },
                        Dates: accountDates
                    };
                })
            );

            // Now filter using Name, Code, InstitutionName
            const filtered = mergedList
                .filter((account) => {
                    return (
                        account?.Name?.toLowerCase().includes(lowercasedValue) ||
                        account?.Code?.toLowerCase().includes(lowercasedValue) ||
                        account?.Customer?.InstitutionName?.toLowerCase().includes(lowercasedValue)
                    );
                })
                .sort((a, b) => {
                    const key = selectedFilter?.orderby;
                    const aVal = a?.[key] || "";
                    const bVal = b?.[key] || "";
                    return aVal.localeCompare(bVal);
                });

            const available = filtered.filter(
                (account) =>
                    account.Customer?.Frequency_count &&
                    account.Dates.length < account.Customer.Frequency_count
            );

            setAvailableItemList(available);

            const selected = filtered.filter(
                (account) =>
                    Array.isArray(account.Dates) &&
                    account.Dates.length > 0 &&
                    (!selectedDate || account.Dates.includes(selectedDate))
            );

            setSelectedItemList(selected);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const handleLongPress = (acc) => {
        setSelectedAccID(acc.ID);
        setShowAccountDetails(true);
    }

    const handleDoublePress = (acc) => {
        setSelectedAccID(acc.ID);
        setShowAccountDetails(true);
    }

    return (
        <div className='planning-sidebar'>
            <div className="filter-section d-flex align-items-center p-2">
                <TauriImage
                    localImage={"/Images/symbols--search.svg"}
                    alt="icon"
                    color={"#7FA6D3"}
                />
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Type to Search"
                        autoComplete="new-password"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div ref={menuRef} style={{ position: "relative" }}>
                    <TauriImage
                        localImage={"/Images/akar-icons--settings-horizontal.svg"}
                        alt="icon"
                        onClick={() => setShowMenu((prev) => !prev)}
                        color={"#7FA6D3"}
                        className={"filter-icon"}
                    />
                    {showMenu && (
                        <div
                            className="opal-settings-menu"
                            style={{
                                position: "absolute",
                                top: "100%",
                                right: 0,
                                width: "15rem",
                                backgroundColor: "#fff",
                                border: "1px solid #B8CCE2",
                                borderRadius: "0.25rem",
                                zIndex: 20,
                                marginTop: "0.25rem",
                                padding: "0.5rem",
                                fontSize: "0.8rem"
                            }}
                        >
                            <div className="row align-items-center mb-2">
                                <div className="col-4 text-nowrap">Group By :</div>
                                <div className="col-8">
                                    <Dropdown>
                                        <Dropdown.Toggle className="form-control text-start">
                                            {groupByOptions.find((g) => g.key === selectedFilter.groupby)?.label}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {groupByOptions.map((item) => (
                                                <Dropdown.Item
                                                    key={item.key}
                                                    onClick={() =>
                                                        setSelectedFilter((prev) => ({ ...prev, groupby: item.key }))
                                                    }>
                                                    {item.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>

                            {/* Order By */}
                            <div className="row align-items-center">
                                <div className="col-4 text-nowrap">Order By :</div>
                                <div className="col-8">
                                    <Dropdown>
                                        <Dropdown.Toggle className="form-control text-start">
                                            {orderByOptions.find((o) => o.key === selectedFilter.orderby)?.label}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {orderByOptions.map((item) => (
                                                <Dropdown.Item
                                                    key={item.key}
                                                    onClick={() =>
                                                        setSelectedFilter((prev) => ({ ...prev, orderby: item.key }))
                                                    }>
                                                    {item.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className='separator'></div>
            <div className="section-tabs d-flex align-items-center p-2">
                <Button
                    className={activeTab === "available" ? "active" : ""}
                    onClick={() => setActiveTab("available")}
                >
                    Available
                </Button>
                <Button
                    className={activeTab === "selected" ? "active" : ""}
                    onClick={() => setActiveTab("selected")}
                >
                    Selected
                </Button>
            </div>
            {loading ? (
                <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
            ) : (
                <OpalAccordionItem
                    items={activeTab === "available" ? availableItemList : selectedItemList}
                    search={search}
                    onSelectItem={selectedItem}
                    showHeader={selectedFilter?.groupby}
                    type={activeTab === "available" ? "v2" : "v3"}
                    selectedCard={selectedCard}
                    setSelectedCard={setSelectedCard}
                    groupby={selectedFilter?.groupby}
                    onDeleteItem={onDeleteItem}
                    onLongPress={handleLongPress}
                    onDoubleClick={handleDoublePress}
                />
            )}

            <AccountDetailsModal show={showAccountDetails} onHide={() => setShowAccountDetails(false)} accID={selectedAccID} />
        </div>
    );
};

export default PlanningSidebar;
